import './ApiDropdownFilterComponent.scss'
import React, {useCallback, useEffect} from "react";
import {
    Button,
    ButtonProps,
    Checkbox,
    CircularProgress,
    ClickAwayListener,
    Divider,
    Grow,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
} from "@mui/material";
import {PageResponse} from "../../../services/ApiTypes";
import {Cancel, FilterAlt, FilterAltOff} from "@mui/icons-material";
import {ApiDropdownSearchComponent} from './ApiDropdownSearchComponent';
import {Option, OptionId, OptionPage, SelectionIds, SelectionResult} from "./ApiDropdownTypes";
import {PPV_FILTER_DEFAULT_SELECT_ALL} from "../../../jobs/koi/purchase-price-variance/classes/PpvConfig";
import {getOppositeTransformOrigin} from '../../../utils/animation';

type Prop = {
    getOptions: (search: string) => Promise<PageResponse<Option>>
    onSelectionChange: (selection: SelectionResult) => void
    label: string
    labelPlural?: string
    sx?: ButtonProps['sx']
    defaultOption?: SelectionIds | null
}
/**
 * Based on https://v5.mui.com/material-ui/react-menu/#menulist-composition
 */
export const ApiDropdownFilterComponent: React.FC<Prop> = ({
                                                               label,
                                                               labelPlural,
                                                               getOptions,
                                                               onSelectionChange,
                                                               sx,
                                                               defaultOption
                                                           }) => {
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const [open, setOpen] = React.useState(false);

    const [loading, setLoading] = React.useState(false);

    /**
     * Unfiltered total
     */
    const [total, setTotal] = React.useState<null | number>(null);

    const [options, setOptions] = React.useState<OptionPage>(null);

    const [selected, setSelected] = React.useState<OptionId[]>(defaultOption ? defaultOption.selected : []);

    /**
     * If it's everything including the selection, or everything excluding the selection
     */
    const [selectIncluding, setSelectIncluding] = React.useState(defaultOption ? defaultOption.inclusive : !PPV_FILTER_DEFAULT_SELECT_ALL);

    const [touched, setTouched] = React.useState(false);

    const [active, setActive] = React.useState(defaultOption ? isActive(defaultOption) : false);

    const labelPlural_ = labelPlural ?? label + 's';

    useEffect(() => {
        (async () => {
            setLoading(true);
            const resp = await getOptions(''); // For demo purposes.
            setLoading(false);
            setOptions({
                search: '',
                page: 1,
                window: resp.results,
                total: resp.count
            });
            setTotal(resp.count)
        })();
    }, [getOptions])

    const onToggleAllSelected = () => {
        if (selected.length > 0) {
            setSelected([]);
            setSelectIncluding(false);
        } else {
            setSelectIncluding(!selectIncluding);
        }

        setTouched(true)
        // onSelectionChange({inclusive: false, selected: []});
    }

    const handleOpen = () => {
        setTouched(false)
        setOpen(true);
    };

    const handleClose = () => {
        if (touched) {
            const s: SelectionResult = {
                inclusive: selectIncluding,
                selected: selected as Option[],
            };
            onSelectionChange(s);
            setActive(isActive(s))
        }

        setTouched(false)
        setOpen(false);
    };

    const cancelFilter = () => {
        if (selected.length > 0) {
            setSelected([]);
            setSelectIncluding(false);

            const s: SelectionResult = {
                inclusive: false,
                selected: [],
            };
            onSelectionChange(s);
            setActive(isActive(s))
        }

        setTouched(false);
        setOpen(false);
    }

    // function handleListKeyDown(event: React.KeyboardEvent) {
    //     if (event.key === 'Tab') {
    //         event.preventDefault();
    //         handleClose();
    //     } else if (event.key === 'Escape') {
    //         handleClose();
    //     }
    // }

    const onClickElement = (o: Option, index: number) => {
        let newSelected: OptionId[];
        if (index !== -1) {
            // Remove element at place index
            newSelected = [...selected];
            newSelected.splice(index, 1);
            setSelected(newSelected);
        } else {
            newSelected = [...selected, o];
            setSelected(newSelected);
        }

        setTouched(true)
        // if (selectIncluding && newSelected.length === 0) {
        //     onSelectionChange({inclusive: false, selected: []});
        // } else {
        //     onSelectionChange({inclusive: selectIncluding, selected: newSelected});
        // }
    }
    const onClickElementOnly = (o: Option) => {
        const newSelected = [o];
        setSelected(newSelected);
        setSelectIncluding(true);

        setTouched(true)
        // onSelectionChange({inclusive: true, selected: newSelected});
    }

    const onClickAllSearchElements = useCallback(() => {
        if (!options) return;
        if (!selectIncluding) return;
        if (options.window.length !== options.total) return;

        // Add all options to the selection
        const currSelectedS = new Set(selected.map(s => s.id));
        const additionalSelected = options.window.filter(o => !currSelectedS.has(o.id));
        const newSelected = [...selected, ...additionalSelected];
        setSelected(newSelected);

        setTouched(true)
        // onSelectionChange({inclusive: true, selected: newSelected});

        // eslint-disable-next-line
    }, [options, selectIncluding, onSelectionChange])


    const menu = <List>
        <ListItem
            secondaryAction={<>
                {loading && <CircularProgress size={24}/>}
                {selected.length > 0 && <IconButton onClick={cancelFilter} size="small"><Cancel/></IconButton>}
            </>}>
            <ListItemText
                primary={
                    selected.length === 0 ? (
                        `No filter on ${labelPlural_}`
                    ) : (
                        selectIncluding
                            ? `Selected ${selected.length} ` + (selected.length === 1 ? label : labelPlural_)
                            : `Excluding ${selected.length} ` + (selected.length === 1 ? label : labelPlural_)
                    )}
                // secondary={total !== null ? `Total ${total} options` : null}
            />


        </ListItem>

        <Divider variant="fullWidth" component="li"/>

        <ListItem>
            <ApiDropdownSearchComponent
                options={options}
                setOptions={setOptions}
                getOptions={getOptions}
                onSelectAllResults={onClickAllSearchElements}
            />
        </ListItem>


        <ListItem disablePadding disableGutters>
            <ListItemButton dense onClick={() => onToggleAllSelected()}>
                <ListItemIcon>
                    <Checkbox
                        edge="start"
                        checked={!selectIncluding}
                        indeterminate={selected.length > 0}
                        onChange={() => onToggleAllSelected()}
                        tabIndex={-1}

                        disableRipple
                        sx={{height: '1em'}}
                    />
                </ListItemIcon>
                <ListItemText
                    secondary="Select all"
                />
            </ListItemButton>
        </ListItem>


        <ListItem disablePadding disableGutters>
            <List sx={{width: '30em', maxHeight: '30em', overflowY: 'scroll'}}>
                {options?.window.map(o => {
                    const index = selected.findIndex(s => s.id === o.id);
                    const checked = selectIncluding ? index !== -1 : index === -1;
                    return <ListItem
                        key={o.id}
                        className="hover-root"
                        disablePadding
                        disableGutters
                        secondaryAction={
                            <Button
                                onClick={() => onClickElementOnly(o)}
                                className="show-on-hover"
                                variant="outlined"
                                size="small">
                                Only
                            </Button>
                        }
                    >
                        <ListItemButton
                            dense
                            onClick={() => onClickElement(o, index)}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    checked={checked}
                                    tabIndex={-1}
                                    disableRipple
                                />
                            </ListItemIcon>
                            <ListItemText primary={o.label}/>
                        </ListItemButton>
                    </ListItem>;
                })}
                {(options?.window.length === 0) && <ListItem>
                    <ListItemText primary={`No results`}/>
                </ListItem>}
            </List>
        </ListItem>
        {options &&
            <ListItem disableGutters disablePadding sx={{padding: '0 1em'}} secondaryAction={
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="small"
                    sx={{mr: 1}}>
                    Ok
                </Button>
            }>
                {options.window.length < options.total &&
                    <ListItemText secondary={`Showing ${options.window.length} of ${options.total} search results`}/>
                }
                {options.window.length === options.total &&
                    <ListItemText secondary={`${options.window.length} search results`}/>
                }
            </ListItem>
        }
    </List>

    return <>
        <Button
            className="api-dropdown-filter-component-button"
            ref={anchorRef}
            variant={active ? 'contained' : 'outlined'}
            disableElevation
            onClick={handleOpen}
            endIcon={active ? <FilterAlt/> : <FilterAltOff/>}
            sx={sx}
        >
            {/*const nSelected = selectIncluding ? selected.length : (total === null || selected.length === 0 ? 0 : total - selected.length);*/}
            {/*{nSelected === 0 ? 'No filter applied'*/}
            {/*    : nSelected === 1 ? `Filtered on 1 option`*/}
            {/*        : `Filtered on ${nSelected} options`}*/}
            {label}
        </Button>

        <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            transition
            // disablePortal
        >
            {({TransitionProps, placement}) => (
                <Grow
                    {...TransitionProps}
                    style={{
                        transformOrigin: getOppositeTransformOrigin(placement),
                    }}
                >
                    <Paper className="api-dropdown-filter-component-popover-root">
                        <ClickAwayListener onClickAway={handleClose}>
                            {menu}
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
    </>
}

function isActive(s: SelectionIds | SelectionResult): boolean {
    return s.selected.length > 0
}
